/**
 * 메시지 사전 — 한국어가 원본, 영어가 번역본.
 *
 * ## 왜 라이브러리를 쓰지 않았나
 *
 * 이 앱의 번역 대상은 화면 문구뿐인데, 한글이 들어간 소스에는 **번역하면 안 되는 것**이
 * 훨씬 많다(아래). 라이브러리를 깔면 "한글이 보이면 t()로 감싼다"는 기계적 작업이
 * 쉬워지고, 그 작업이 파서를 조용히 망가뜨린다. 그래서 사전을 명시적으로 두고
 * **키를 하나씩 옮기는** 방식을 택했다. 의존성도 늘지 않는다.
 *
 * ## 절대 번역하면 안 되는 것 (이 파일에 넣지 말 것)
 *
 * - `utils/header-detector.ts` · `services/coupangParser.ts` — 한국 은행·카드사 CSV의
 *   **실제 컬럼명**('날짜'·'결제금액'·'주문번호')과 매칭한다. 번역하면 업로드가 깨진다.
 * - `services/merchantNormalizer.ts` — 한글 가맹점명 정규화 규칙.
 * - `data/mock-report/**` · `data/excel-ground-truth.ts` — 테스트 픽스처.
 *
 * ## 카테고리 시드는 별도 문제
 *
 * `services/category/defaults.ts`의 '지출·식비·외식'은 UI 문구가 아니라 **가입 시
 * 사용자 계정에 복사되는 데이터**다. 영어 사용자에게는 영어 시드를 줘야 하지만,
 * 기존 사용자의 데이터를 바꾸면 안 된다. 이 파일이 아니라 시드 단계에서 다룬다.
 *
 * ## 완전성 보장
 *
 * `en`은 `ko`의 모든 키를 가져야 한다 — `Messages` 타입이 `typeof ko`에서 파생되므로
 * 키를 빠뜨리면 **컴파일 오류**가 난다. 런타임 폴백에 기대지 않는다.
 */

export const ko = {
  common: {
    appName: '모닛',
    or: '또는',
    loading: '불러오는 중...',
    cancel: '취소',
    confirm: '확인',
    retry: '다시 시도',
  },
  auth: {
    emailLabel: '이메일',
    passwordLabel: '비밀번호',
    showPassword: '비밀번호 보기',
    hidePassword: '비밀번호 숨기기',
    signIn: '로그인',
    signingIn: '로그인 중...',
    signInWithGoogle: 'Google로 로그인',
    signInWithApple: 'Apple로 로그인',
    signInWithEmail: '이메일/비밀번호',
    emailRequired: '이메일을 입력해주세요.',
    emailInvalid: '유효한 이메일 주소를 입력해주세요.',
    passwordRequired: '비밀번호를 입력해주세요.',
    signInCanceled: '로그인이 취소되었습니다.',
    signUp: '회원가입',
    haveAccount: '이미 계정이 있으신가요?',
    nameLabel: '이름',
    namePlaceholder: '홍길동',
    confirmPasswordLabel: '비밀번호 확인',
    passwordStrength: '비밀번호 강도',
    strength0: '매우 약함',
    strength1: '약함',
    strength2: '보통',
    strength3: '강함',
    strength4: '매우 강함',
    strengthNeeds: '필요',
    termsLabel: '서비스 약관',
    privacyLabel: '개인정보 처리방침',
    marketingLabel: '혜택·소식 알림 수신',
    required: '필수',
    optional: '선택',
    optionalNote: '선택 항목에 동의하지 않아도 가입하고 모든 기능을 쓸 수 있어요.',
    nameMin: '이름은 최소 2자 이상이어야 합니다.',
    nameMax: '이름은 최대 50자까지 가능합니다.',
    passwordMin: '비밀번호는 최소 8자 이상이어야 합니다.',
    passwordComplexity: '비밀번호는 대문자, 소문자, 숫자, 특수문자를 포함해야 합니다.',
    confirmPasswordRequired: '비밀번호 확인을 입력해주세요.',
    passwordMismatch: '비밀번호가 일치하지 않습니다.',
    agreeTermsRequired: '서비스 약관에 동의해주세요.',
    agreePrivacyRequired: '개인정보 처리방침에 동의해주세요.',
  },
} as const;

/** ko 에서 파생 — en 이 키를 빠뜨리면 컴파일 오류 */
export type Messages = {
  [S in keyof typeof ko]: { [K in keyof (typeof ko)[S]]: string };
};

export const en: Messages = {
  common: {
    appName: 'Monit',
    or: 'or',
    loading: 'Loading…',
    cancel: 'Cancel',
    confirm: 'OK',
    retry: 'Try again',
  },
  auth: {
    emailLabel: 'Email',
    passwordLabel: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signInWithGoogle: 'Sign in with Google',
    signInWithApple: 'Sign in with Apple',
    signInWithEmail: 'Email and password',
    emailRequired: 'Enter your email address.',
    emailInvalid: 'Enter a valid email address.',
    passwordRequired: 'Enter your password.',
    signInCanceled: 'Sign-in was canceled.',
    signUp: 'Create account',
    haveAccount: 'Already have an account?',
    nameLabel: 'Name',
    namePlaceholder: 'Jane Doe',
    confirmPasswordLabel: 'Confirm password',
    passwordStrength: 'Password strength',
    strength0: 'Very weak',
    strength1: 'Weak',
    strength2: 'Fair',
    strength3: 'Strong',
    strength4: 'Very strong',
    strengthNeeds: 'Needs',
    termsLabel: 'Terms of Service',
    privacyLabel: 'Privacy Policy',
    marketingLabel: 'Receive news and offers',
    required: 'Required',
    optional: 'Optional',
    optionalNote: 'You can sign up and use every feature without agreeing to the optional item.',
    nameMin: 'Name must be at least 2 characters.',
    nameMax: 'Name must be 50 characters or fewer.',
    passwordMin: 'Password must be at least 8 characters.',
    passwordComplexity: 'Password must include an uppercase letter, a lowercase letter, a number, and a symbol.',
    confirmPasswordRequired: 'Confirm your password.',
    passwordMismatch: 'Passwords do not match.',
    agreeTermsRequired: 'Please agree to the Terms of Service.',
    agreePrivacyRequired: 'Please agree to the Privacy Policy.',
  },
};

export const MESSAGES = { ko, en } as const;
export type Locale = keyof typeof MESSAGES;
export const LOCALES = Object.keys(MESSAGES) as Locale[];
